/**
 * Logic behind the Save Order customer picker (one list: walk in, your
 * customers, phone contacts). Kept free of React so it can be tested: who the
 * order is for, what a search shows, and which rows the list draws.
 */
import { WALK_IN_CUSTOMER, isWalkInCustomer } from './saleCustomer';

export type PickerCustomer = {
  name: string;
  count: number;
  customerId: string | null;
  isCloud: boolean;
};

export type PickerContact = {
  id: string;
  name: string;
  /** The number as the phone shows it, e.g. "0722 418 903". */
  phone: string;
};

/** Who the order is for, read off the name and id the sheet holds. */
export type OrderCustomerChoice =
  | { kind: 'none' }
  | { kind: 'walkIn' }
  | { kind: 'customer'; name: string; customerId: string | null }
  /** A typed order name such as a table number, not a saved customer. */
  | { kind: 'label'; name: string };

export type ContactsListStatus =
  | 'hidden'
  | 'undetermined'
  | 'denied'
  | 'blocked'
  | 'loading'
  | 'ready'
  | 'error';

export type ContactRowState = 'saving' | 'error';

export type PickerRow =
  | { type: 'section'; key: string; title: string; trailing?: string }
  | { type: 'walkIn'; key: string; selected: boolean }
  | { type: 'label'; key: string; name: string; selected: boolean }
  | {
      type: 'customer';
      key: string;
      customer: PickerCustomer;
      selected: boolean;
      meta: string;
      /** A name known only from past orders: its add button, or the save in flight. */
      addToCustomers: 'ready' | 'saving' | null;
      highlight: TextMatch;
    }
  | {
      type: 'contact';
      key: string;
      contact: PickerContact;
      state: ContactRowState | null;
      highlight: TextMatch;
    }
  | {
      type: 'contactsPermission';
      key: string;
      status: 'undetermined' | 'denied' | 'blocked' | 'loading' | 'error';
    }
  | { type: 'contactsEmpty'; key: string }
  | { type: 'customersError'; key: string }
  /** No customers saved at this location yet. */
  | { type: 'customersEmpty'; key: string }
  | { type: 'noMatch'; key: string; query: string }
  | { type: 'useLabel'; key: string; name: string }
  | { type: 'addNew'; key: string; name: string }
  | { type: 'addNewLink'; key: string }
  | {
      type: 'more';
      key: string;
      hidden: number;
      what: 'customers' | 'contacts';
    };

export type TextMatch = { pre: string; hit: string; post: string };

/**
 * Who a name refers to: the name as typed, trimmed because that is how an
 * order stores it. Case is kept, so "moses" and "Moses" are two people and
 * their orders are never pooled — the same rule the server links orders by.
 */
export const customerKey = (name: string): string => String(name || '').trim();

/** For searching only: a query matches a name whatever case either is in. */
export const normalizeName = (name: string): string =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const digitsOf = (value: string): string =>
  String(value || '').replace(/\D/g, '');

export const describeChoice = (
  name: string,
  customerId: string | null,
  customers: PickerCustomer[],
): OrderCustomerChoice => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return { kind: 'none' };
  if (isWalkInCustomer(trimmed)) return { kind: 'walkIn' };
  if (customerId) return { kind: 'customer', name: trimmed, customerId };
  const key = customerKey(trimmed);
  const listed = customers.find(c => customerKey(c.name) === key);
  if (listed) {
    return {
      kind: 'customer',
      name: listed.name,
      customerId: listed.customerId,
    };
  }
  return { kind: 'label', name: trimmed };
};

/** Name shown for a choice on the save button and the cart bar. */
export const choiceName = (choice: OrderCustomerChoice): string => {
  switch (choice.kind) {
    case 'walkIn':
      return WALK_IN_CUSTOMER;
    case 'customer':
    case 'label':
      return choice.name;
    default:
      return '';
  }
};

/** The order customers are listed in: most orders first, then by name. */
export const compareCustomers = <T extends { name: string; count?: number }>(
  a: T,
  b: T,
  countOf: (customer: T) => number = customer => customer.count || 0,
): number => countOf(b) - countOf(a) || a.name.localeCompare(b.name);

/** Exact names first, then names that start with the query, then busiest. */
export const filterCustomers = (
  customers: PickerCustomer[],
  query: string,
  /** The count to place each customer by; their own by default. */
  countOf?: (customer: PickerCustomer) => number,
): PickerCustomer[] => {
  const q = normalizeName(query);
  if (!q) return customers;
  return customers
    .filter(c => normalizeName(c.name).includes(q))
    .sort((a, b) => {
      const an = normalizeName(a.name);
      const bn = normalizeName(b.name);
      if (an === q && bn !== q) return -1;
      if (bn === q && an !== q) return 1;
      const aStarts = an.startsWith(q);
      const bStarts = bn.startsWith(q);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      return compareCustomers(a, b, countOf);
    });
};

/** Matches the name, or the number once three or more digits are typed. */
export const filterContacts = (
  contacts: PickerContact[],
  query: string,
): PickerContact[] => {
  const q = normalizeName(query);
  if (!q) return contacts;
  const qDigits = digitsOf(q);
  return contacts.filter(contact => {
    if (normalizeName(contact.name).includes(q)) return true;
    return qDigits.length >= 3 && digitsOf(contact.phone).includes(qDigits);
  });
};

/** Contacts who are already customers (same name) show only as customers. */
export const contactsNotInCustomers = (
  contacts: PickerContact[],
  customers: PickerCustomer[],
): PickerContact[] => {
  const taken = new Set(customers.map(c => customerKey(c.name)));
  return contacts.filter(contact => !taken.has(customerKey(contact.name)));
};

export const hasExactCustomerMatch = (
  customers: PickerCustomer[],
  query: string,
): PickerCustomer | undefined => {
  const q = customerKey(query);
  if (!q) return undefined;
  return customers.find(c => customerKey(c.name) === q);
};

/** Where the query sits in a name, so the list can bold it. */
export const matchText = (text: string, query: string): TextMatch => {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  const at = q ? text.toLowerCase().indexOf(q) : -1;
  if (at < 0) return { pre: text, hit: '', post: '' };
  return {
    pre: text.slice(0, at),
    hit: text.slice(at, at + q.length),
    post: text.slice(at + q.length),
  };
};

/**
 * A contact's number in the form the New Customer form saves ("+254712…"),
 * so the server's duplicate check sees one number however it was typed.
 * Local numbers ("0712…", "712…") take the default calling code.
 */
export const toE164Phone = (
  raw: string,
  defaultCallingCode: string,
): string => {
  const cc = digitsOf(defaultCallingCode);
  const value = String(raw || '').trim();
  if (!value) return '';
  let digits = digitsOf(value);
  let international = value.startsWith('+');
  if (!international && digits.startsWith('00')) {
    digits = digits.slice(2);
    international = true;
  }
  if (!international) {
    if (digits.startsWith('0')) {
      digits = cc + digits.slice(1);
    } else if (cc && !digits.startsWith(cc)) {
      digits = cc + digits;
    }
  }
  return digits.length >= 7 ? `+${digits}` : '';
};

export const pluralize = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/** "12 orders", or "New customer" for one with no orders yet. */
export const customerMeta = (customer: PickerCustomer): string =>
  customer.count > 0 ? pluralize(customer.count, 'order') : 'New customer';

export type RawPhoneContact = {
  recordID?: string | null;
  displayName?: string | null;
  givenName?: string | null;
  middleName?: string | null;
  familyName?: string | null;
  company?: string | null;
  phoneNumbers?: Array<{ number?: string | null }> | null;
};

/**
 * The phone's contacts as the picker lists them: one row per person with a
 * number, named from the display name or the name parts, deduplicated when
 * the same person is saved twice, A to Z.
 */
export const normalizeRawContacts = (
  raw: RawPhoneContact[],
): PickerContact[] => {
  const seen = new Set<string>();
  const list: PickerContact[] = [];
  for (const contact of raw || []) {
    const parts = [contact.givenName, contact.middleName, contact.familyName]
      .map(part => String(part || '').trim())
      .filter(Boolean);
    const name =
      String(contact.displayName || '').trim() ||
      parts.join(' ') ||
      String(contact.company || '').trim();
    const phone = String(
      (contact.phoneNumbers || []).find(entry =>
        String(entry?.number || '').trim(),
      )?.number || '',
    )
      .trim()
      .replace(/\s+/g, ' ');
    if (!name || !phone) continue;
    // "0712 345 678" and "+254712345678" are one number: compare the last
    // nine digits, the local part of a Kenyan number.
    const key = `${normalizeName(name)}|${digitsOf(phone).slice(-9)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    list.push({
      id: String(contact.recordID || key),
      name,
      phone,
    });
  }
  return list.sort((a, b) => a.name.localeCompare(b.name));
};

export type BuildPickerRowsInput = {
  query: string;
  choice: OrderCustomerChoice;
  customers: PickerCustomer[];
  /** The customer list could not be fetched and nothing is cached. */
  customersError?: boolean;
  contacts: PickerContact[];
  contactsStatus: ContactsListStatus;
  /** Contacts being saved as customers, or whose save failed, by contact id. */
  contactRowState?: Record<string, ContactRowState>;
  /** Customers created from this sheet, by id, with the phone they came with. */
  createdCustomers?: Record<string, { phone?: string; matched?: boolean }>;
  /** Order names being saved as customers: normalised name → name. */
  savingNames?: Record<string, string>;
  /**
   * Contacts tapped in this sheet: contact id → the customer they were saved
   * as, or null while saving. Each keeps its place in the list.
   */
  tappedContacts?: Record<string, string | null>;
  /**
   * Order names saved as customers from this sheet: normalised name → the
   * order count they were listed with when their add button was tapped.
   */
  heldCounts?: Record<string, number>;
  /** Rows shown before a "search to see more" note; none means all. */
  limits?: { customers: number; contacts: number };
};

/** Listed from the names on past orders only; not in Back Office customers. */
export const isOrderNameOnly = (customer: PickerCustomer): boolean =>
  !customer.isCloud && !customer.customerId;

const isSelectedCustomer = (
  choice: OrderCustomerChoice,
  customer: PickerCustomer,
): boolean => {
  if (choice.kind !== 'customer') return false;
  if (choice.customerId && customer.customerId) {
    return choice.customerId === customer.customerId;
  }
  return customerKey(choice.name) === customerKey(customer.name);
};

/** The rows the list draws, top to bottom, for what the picker holds. */
export const buildPickerRows = ({
  query,
  choice,
  customers: allCustomers,
  customersError = false,
  contacts,
  contactsStatus,
  contactRowState = {},
  createdCustomers = {},
  savingNames = {},
  tappedContacts = {},
  heldCounts = {},
  limits,
}: BuildPickerRowsInput): PickerRow[] => {
  const rows: PickerRow[] = [];
  // Walk in has its own row at the top; orders saved as walk in must not
  // list it again among the customers.
  const customers = allCustomers.filter(c => !isWalkInCustomer(c.name));
  // A name saved from this sheet keeps its place: the saved record carries
  // the server's count of its orders, which can differ from the one listed
  // here, so it is placed by the count it had when tapped. Its text shows
  // the new count.
  const countOf = (customer: PickerCustomer) => {
    const key = customerKey(customer.name);
    return Object.prototype.hasOwnProperty.call(heldCounts, key)
      ? heldCounts[key]
      : customer.count || 0;
  };
  if (Object.keys(heldCounts).length > 0) {
    customers.sort((a, b) => compareCustomers(a, b, countOf));
  }
  const q = normalizeName(query);
  const searching = q.length > 0;

  // A tapped contact keeps its place, shown as the customer it became, so
  // the row does not jump. That customer is found by id once the save
  // returns, and by the contact's name before then: the store can have the
  // new customer a render earlier. One made from the contact (its name) is
  // listed only there, so nothing above it shifts; one matched by phone has
  // another name and stays in Your customers as well.
  const placed = new Map<string, PickerCustomer>();
  const madeHere = new Set<string>();
  if (contactsStatus === 'ready') {
    contacts.forEach(contact => {
      const customerId = tappedContacts[contact.id];
      if (customerId === undefined) return;
      const key = customerKey(contact.name);
      const customer =
        (customerId && customers.find(c => c.customerId === customerId)) ||
        customers.find(c => customerKey(c.name) === key);
      if (!customer) return;
      placed.set(contact.id, customer);
      if (customerKey(customer.name) === key) madeHere.add(key);
    });
  }
  const listedCustomers = customers.filter(
    c => !madeHere.has(customerKey(c.name)),
  );
  const notCustomers = new Set(
    contactsNotInCustomers(contacts, customers).map(c => c.id),
  );
  const visibleContacts = contacts.filter(
    c => placed.has(c.id) || notCustomers.has(c.id),
  );

  const customerRow = (customer: PickerCustomer): PickerRow => {
    const created = customer.customerId
      ? createdCustomers[customer.customerId]
      : undefined;
    let meta = customerMeta(customer);
    if (created?.matched) meta = `${meta} · matched by phone`;
    else if (created?.phone) meta = created.phone;
    let addToCustomers: 'ready' | 'saving' | null = null;
    if (isOrderNameOnly(customer)) {
      addToCustomers = savingNames[customerKey(customer.name)]
        ? 'saving'
        : 'ready';
    }
    return {
      type: 'customer',
      key: `customer:${customer.customerId || customerKey(customer.name)}`,
      customer,
      selected: isSelectedCustomer(choice, customer),
      meta,
      addToCustomers,
      highlight: matchText(customer.name, query),
    };
  };

  const contactRow = (contact: PickerContact): PickerRow => {
    const customer = placed.get(contact.id);
    // The contact's key, so the list updates the row where it is.
    if (customer) {
      return { ...customerRow(customer), key: `contact:${contact.id}` };
    }
    return {
      type: 'contact',
      key: `contact:${contact.id}`,
      contact,
      state: contactRowState[contact.id] || null,
      highlight: matchText(contact.name, query),
    };
  };

  /** The contacts section header and the allow / settings / loading / retry card. */
  const pushContactsPermission = (status: ContactsListStatus) => {
    if (status === 'hidden' || status === 'ready') return;
    rows.push({
      type: 'section',
      key: 'section:contacts',
      title: 'Phone contacts',
    });
    rows.push({
      type: 'contactsPermission',
      key: 'contactsPermission',
      status,
    });
  };

  if (!searching) {
    if (choice.kind === 'label') {
      rows.push({ type: 'section', key: 'section:order', title: 'This order' });
      rows.push({
        type: 'label',
        key: 'label',
        name: choice.name,
        selected: true,
      });
    }
    rows.push({
      type: 'walkIn',
      key: 'walkIn',
      selected: choice.kind === 'walkIn',
    });

    rows.push({
      type: 'section',
      key: 'section:customers',
      title: 'Your customers & Contacts',
      trailing:
        customers.length > 0
          ? String(customers.length)
          : customersError
            ? '—'
            : undefined,
    });
    if (customersError && customers.length === 0) {
      rows.push({ type: 'customersError', key: 'customersError' });
    } else if (customers.length === 0) {
      rows.push({ type: 'customersEmpty', key: 'customersEmpty' });
    } else {
      const shown = limits
        ? listedCustomers.slice(0, limits.customers)
        : listedCustomers;
      shown.forEach(customer => rows.push(customerRow(customer)));
      if (limits && listedCustomers.length > shown.length) {
        rows.push({
          type: 'more',
          key: 'more:customers',
          hidden: listedCustomers.length - shown.length,
          what: 'customers',
        });
      }
    }

    if (contactsStatus === 'ready') {
      rows.push({
        type: 'section',
        key: 'section:contacts',
        title: 'Phone contacts',
        trailing: visibleContacts.some(c => !placed.has(c.id))
          ? 'Tap to add to customers'
          : undefined,
      });
      if (visibleContacts.length === 0) {
        rows.push({ type: 'contactsEmpty', key: 'contactsEmpty' });
      } else {
        const shown = limits
          ? visibleContacts.slice(0, limits.contacts)
          : visibleContacts;
        shown.forEach(contact => rows.push(contactRow(contact)));
        if (limits && visibleContacts.length > shown.length) {
          rows.push({
            type: 'more',
            key: 'more:contacts',
            hidden: visibleContacts.length - shown.length,
            what: 'contacts',
          });
        }
      }
    } else {
      pushContactsPermission(contactsStatus);
    }
    return rows;
  }

  const customerMatches = filterCustomers(listedCustomers, query, countOf);
  const contactMatches =
    contactsStatus === 'ready' ? filterContacts(visibleContacts, query) : [];
  // Contacts not read yet (not allowed, loading, failed) were not searched,
  // so keep asking for them instead of saying nothing matched.
  const contactsUnread =
    contactsStatus !== 'ready' && contactsStatus !== 'hidden';

  if (customerMatches.length === 0 && contactMatches.length === 0) {
    const name = String(query).trim();
    if (contactsUnread) pushContactsPermission(contactsStatus);
    else rows.push({ type: 'noMatch', key: 'noMatch', query: name });
    rows.push({ type: 'useLabel', key: 'useLabel', name });
    rows.push({ type: 'addNew', key: 'addNew', name });
    return rows;
  }

  if (customerMatches.length > 0) {
    rows.push({
      type: 'section',
      key: 'section:customers',
      title: 'Your customers & Contacts',
      trailing: pluralize(customerMatches.length, 'match').replace(
        'matchs',
        'matches',
      ),
    });
    customerMatches.forEach(customer => rows.push(customerRow(customer)));
  }
  if (contactMatches.length > 0) {
    rows.push({
      type: 'section',
      key: 'section:contacts',
      title: 'Phone contacts',
      trailing: pluralize(contactMatches.length, 'match').replace(
        'matchs',
        'matches',
      ),
    });
    contactMatches.forEach(contact => rows.push(contactRow(contact)));
  }
  pushContactsPermission(contactsStatus);
  rows.push({ type: 'addNewLink', key: 'addNewLink' });
  return rows;
};
